import { Link } from "react-router"
import { useQueryClient } from "@tanstack/react-query"

import HorizontalImageNotification from "../../components/HorizontalImageNotification"
import {
  useAccountId,
  useAccountUrlPrefix,
  useGetGovtAppWizard,
} from "../../hooks"
import ENTRY_CARD_IMG from "../../images/spb-wizard-card.svg"
import CARD_IMAGE from "../../images/empty-card-circle.svg"
import { GovtApWizardType } from "@/types/govtAppWizard"

export interface GovtAppNotificationsProps {
  isMember: boolean
}

const GovtAppNotifications = ({ isMember }: GovtAppNotificationsProps) => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()
  const accountUrlPrefix = useAccountUrlPrefix()

  const { data: govtAppWizard, isLoading: govtAppWizardIsLoading } =
    useGetGovtAppWizard<GovtApWizardType, Error>(queryClient, accountId)

  if (govtAppWizardIsLoading || govtAppWizard === undefined) {
    return null
  }

  if (
    govtAppWizard?.has_been_started == false &&
    govtAppWizard?.completed == false
  ) {
    return (
      <div className="bg-cloud-50 flex gap-4 border-2 border-[#ECA674] rounded-sm p-4">
        <div className="shrink-0">
          <img src={ENTRY_CARD_IMG} alt="spb" width={84} height={138} />
        </div>

        <div>
          <h3 className="font-bold leading-[130%] font-body tracking-[0.32px]">
            Get cost share programs to build your better forest for the future
          </h3>

          <p className="font-body leading-[130%] tracking-[0.32px] mt-2">
            Take our quick quiz to get a personalized stack of programs that
            give you options to do the things you want to do on your land to
            meet your goals.
          </p>

          <Link
            to={`${accountUrlPrefix}/checkup/government`}
            className="govt-app-widget-get-started btn2 btn2-primary font-bold px-6 py-[11px] mt-4"
          >
            Get Started
          </Link>
        </div>
      </div>
    )
  }

  if (
    govtAppWizard?.has_been_started === true &&
    govtAppWizard?.completed === false &&
    govtAppWizard?.selected_action_keys === null &&
    govtAppWizard?.selected_implementation === null
  ) {
    return (
      <HorizontalImageNotification
        image={CARD_IMAGE}
        heading="You started the NRCS Guide to personalized practice stacks"
        variant="white"
        className="gap-4"
        closeable
      >
        <p>
          Looks like you've answered all the eligibility questions and have
          received recommended practice stacks. Get back in to save your
          recommendations.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mt-3">
          <Link
            to={`${accountUrlPrefix}/checkup/government?step=3`}
            className="btn2 btn2-primary font-bold"
          >
            Jump back in
          </Link>
        </div>
      </HorizontalImageNotification>
    )
  }

  if (
    govtAppWizard?.has_been_started === true &&
    govtAppWizard?.completed === false &&
    Array.isArray(govtAppWizard?.selected_action_keys) &&
    govtAppWizard?.selected_implementation === null
  ) {
    return (
      <HorizontalImageNotification
        image={CARD_IMAGE}
        heading="Finish the NRCS Guide to personalized practice stacks"
        variant="white"
        className="gap-4"
        closeable
      >
        <p>
          Looks like you're almost done with our guide. You have your
          recommended practice stacks, but haven’t finished with your Plan
          Summary.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mt-3">
          <Link
            to={`${accountUrlPrefix}/checkup/government?step=4`}
            className="btn2 btn2-primary font-bold"
          >
            Jump back in
          </Link>
        </div>
      </HorizontalImageNotification>
    )
  }

  if (govtAppWizard.completed) {
    return (
      <HorizontalImageNotification
        image={CARD_IMAGE}
        heading="You have new Saved Programs from your NRCS Guide"
        variant="white"
        className="gap-4"
        closeable
      >
        <p>
          Your selected stacks for NRCS are in your Programs tab under Saved
          Programs. Access those at anytime as you go through the process to
          implement your plans.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mt-3">
          <Link
            to={`${accountUrlPrefix}/programs/page/1?category=saved_programs`}
            className="btn2 btn2-primary font-bold"
          >
            View Saved Programs
          </Link>

          <Link
            to={`${accountUrlPrefix}/checkup/government${
              isMember ? "?step=5" : "/paywall"
            }`}
            className="btn2 btn2-outline-primary font-bold "
          >
            View Plan Summary
          </Link>
        </div>
      </HorizontalImageNotification>
    )
  }

  return null
}

export default GovtAppNotifications
